/**
 * Customer Contract Types
 * Ügyfél szerződések típusai órakeret kezeléssel
 */

import { Timestamp } from 'firebase/firestore';

export type ContractType = 'fixed_term' | 'indefinite' | 'project_based';
export type ContractStatus = 'active' | 'suspended' | 'expired' | 'terminated';
export type BillingFrequency = 'monthly' | 'quarterly' | 'yearly' | 'project_end' | 'hourly';
export type ServiceBillingType = 'one_time' | 'recurring';

export interface HourPool {
  includedHours: number; // Szerződésben foglalt órák (pl. havi 5 óra)
  usedHours: number; // Felhasznált órák
  remainingHours: number; // Maradék órák
  overageHours: number; // Túlóra (számlázandó)
  resetPeriod: 'monthly' | 'quarterly' | 'yearly' | 'never'; // Mikor nullázódik
  lastResetDate?: Date | Timestamp; // Utolsó nullázás dátuma
  overageRate?: number; // Túlóra díja (Ft/óra)
}

export interface ContractService {
  productId: string;
  productName: string;
  productCode: string;
  productType?: 'product' | 'service' | 'package'; // Termék típusa
  description?: string;
  quantity: number;
  
  // Díjtípus - FONTOS!
  billingType: 'one_time' | 'recurring'; // Egyszeri vagy ismétlődő díj
  
  // Árazás
  listPrice: number; // Globális listaár (a termékből)
  contractPrice: number; // Szerződéses egyedi ár (amit ténylegesen alkalmazunk)
  unitPrice: number; // Alkalmazott egységár (= contractPrice, kompatibilitás miatt)
  totalPrice: number; // contractPrice * quantity
  discount?: number; // Kedvezmény % (ha van)
  discountAmount?: number; // Kedvezmény összege Ft-ban
  taxRate?: string;
  
  // Órakeret (opcionális, csak szolgáltatásoknál)
  hourPool?: HourPool;
  
  // Csomag tartalom (opcionális, csak csomagoknál)
  packageServices?: PackageServiceItem[];
  
  // Megjegyzés az egyedi árról
  priceNote?: string; // Pl.: "Kedvezményes ár hosszú távú szerződés miatt"
}

// Csomag tartalom elem
export interface PackageServiceItem {
  productId: string;
  productCode: string;
  productName: string;
  quantity: number;
  unit?: string;
  hours?: number; // Óraszám (ha szolgáltatás)
  isIncluded: boolean; // Benne van az alapdíjban?
}

export interface CustomerContract {
  id: string;
  organizationId: string;
  
  // Ügyfél
  customerId: string;
  customerName: string; // denormalizált
  
  // Szerződés alapadatok
  contractNumber: string; // Szerződésszám (pl. SZ-2024-001)
  contractName: string; // Szerződés neve
  contractType: ContractType;
  status: ContractStatus;
  
  // Generálás forrása (opcionális)
  generatedFrom?: {
    type: 'manual' | 'quotation' | 'project' | 'recurring_order' | 'template';
    sourceId?: string; // Ajánlat ID, Projekt ID, stb.
    sourceName?: string; // Forrás neve
    generatedAt?: Date | Timestamp;
  };
  
  // Szolgáltatások
  services: ContractService[];
  
  // Időtartam
  startDate: Date | Timestamp;
  endDate?: Date | Timestamp; // Csak határozott időnél
  
  // Pénzügy
  monthlyFee: number; // Havi díj
  billingFrequency: BillingFrequency;
  currency: string; // HUF, EUR, USD
  
  // Számlázás
  nextBillingDate?: Date | Timestamp;
  lastBillingDate?: Date | Timestamp;
  invoiceIds?: string[]; // Kapcsolódó számlák
  
  // Task-ok
  taskIds?: string[]; // Kapcsolódó task-ok
  
  // Megjegyzések
  notes?: string;
  terms?: string; // Szerződési feltételek
  
  // Meta
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
  createdBy: string;
  createdByName: string;
  
  // Automatikus megújítás
  autoRenew?: boolean;
  renewalNoticeDays?: number; // Hány nappal a lejárat előtt értesítsen
}

export interface ContractFormData {
  customerId: string;
  contractName: string;
  contractType: ContractType;
  services: ContractService[];
  startDate: Date;
  endDate?: Date;
  monthlyFee: number;
  billingFrequency: BillingFrequency;
  currency?: string;
  notes?: string;
  terms?: string;
  autoRenew?: boolean;
  renewalNoticeDays?: number;
  generatedFrom?: {
    type: 'manual' | 'quotation' | 'project' | 'recurring_order' | 'template';
    sourceId?: string;
    sourceName?: string;
  };
}

// Szerződés template (sablonok)
export interface ContractTemplate {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  contractType: ContractType;
  services: Omit<ContractService, 'productId' | 'productName' | 'productCode'>[]; // Szolgáltatások template-je
  defaultMonthlyFee: number;
  defaultBillingFrequency: BillingFrequency;
  defaultCurrency: string;
  defaultTerms?: string;
  defaultAutoRenew?: boolean;
  defaultRenewalNoticeDays?: number;
  isActive: boolean;
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
  createdBy: string;
}

export interface ContractStats {
  totalContracts: number;
  activeContracts: number;
  expiringSoon: number; // 30 napon belül lejáró
  totalMonthlyRevenue: number;
  totalYearlyRevenue: number;
}

// Szerződés típus opciók
export const CONTRACT_TYPE_OPTIONS = [
  { value: 'fixed_term', label: 'Határozott idejű', icon: '📅' },
  { value: 'indefinite', label: 'Határozatlan idejű', icon: '♾️' },
  { value: 'project_based', label: 'Projekt alapú', icon: '🎯' }
] as const;

// Szerződés státusz opciók
export const CONTRACT_STATUS_OPTIONS = [
  { value: 'active', label: 'Aktív', color: 'green' },
  { value: 'suspended', label: 'Felfüggesztve', color: 'yellow' },
  { value: 'expired', label: 'Lejárt', color: 'red' },
  { value: 'terminated', label: 'Lezárt', color: 'gray' }
] as const;

// Számlázási gyakoriság opciók
export const BILLING_FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Havi', icon: '📆' },
  { value: 'quarterly', label: 'Negyedéves', icon: '📊' },
  { value: 'yearly', label: 'Éves', icon: '📅' },
  { value: 'project_end', label: 'Projekt végén', icon: '🎯' },
  { value: 'hourly', label: 'Óradíjas', icon: '⏱️' }
] as const;
